import React, { useContext } from 'react'
import { Link } from 'gatsby'
import styled from '@emotion/styled'
import { JsonLd } from 'react-schemaorg'
import { node, object } from 'prop-types'
import { useLocation } from '@reach/router'

//Import Components
import { Layout, SearchSchema, SimpleHeader, SiteConfigContext } from '@jelly/components'

//Styled Components via Emotion
const PageLink = styled(Link)`
	color: ${({ theme }) => theme.colors.primary};
	font-size: 1.2rem;
	letter-spacing: 0.85px;
	line-height: 35px;
	opacity: ${({ isbuyingguides }) => (isbuyingguides === 'true' ? 1 : 0.5)};

	&:hover {
		color: ${({ theme }) => theme.colors.tertiary};
	}

	${({ theme }) => theme.MQ.XL} {
		font-size: 1.75rem;
		letter-spacing: 5px;
		line-height: 70px;
	}
`
const PageWrapper = styled.div`
	display: block;
	justify-content: space-around;
	min-height: 100vh;
	padding: 1rem 1rem 2rem;

	${({ theme }) => theme.MQ.XL} {
		justify-content: start;
		margin: 0 auto;
		padding-left: 35px;
	}
`
const SiteMapWrapper = styled.div`
	height: 100%;
	width: 100%;
`
const ContentWrapper = styled.div`
	display: grid;
	grid-gap: 10px;
	grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
`
const SiteMapCategories = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;

	${({ theme }) => theme.MQ.XL} {
		justify-content: stretch;
	}
`
const Divider = styled.div`
	color: ${({ theme }) => theme.colors.gray[400]};
	font-size: 20px;
	font-weight: 200;
	line-height: 35px;
	padding: 0 10px;

	${({ theme }) => theme.MQ.XL} {
		font-size: 40px;
		line-height: 70px;
		padding: 0 15px;
	}
`

/**
 * This function is here to determine which "category" text and slug to use for the sitemap link to categories
 *
 * @param {boolean} isBdc :: flag to determine if we are on business.com or not
 */
function getCorrectBuyingGuideTitleAndSlug(isBdc) {
	if (isBdc) {
		return { categoryTitle: 'Buying Guides', categorySlug: 'buying-guides' }
	}
	return { categoryTitle: 'Best Picks', categorySlug: 'best-picks' }
}

Sitemap.propTypes = {
	children: node.isRequired,
	meta: object,
}

/**
 * This is the Sitemap Page index, pulls together the whole sitemap
 *
 * @param {Node} props.children :: Contents that will be wrapped by the Sitemap
 * @param {Object} props.meta :: meta data object
 */
export function Sitemap({ children, meta }) {
	const {
		siteConstants: { SITE_HOST },
	} = useContext(SiteConfigContext)

	const isBdc = SITE_HOST.includes('business.com')
	const { categoryTitle, categorySlug } = getCorrectBuyingGuideTitleAndSlug(isBdc)

	let { pathname } = useLocation()
	const [year] = pathname.replace('/sitemap/', '').split('/')
	const isBuyingGuidesPath = pathname.includes('buying-guides')
	const isBestPickPath = pathname.includes('best-picks')
	const isOfTypeBuyingGuide = isBuyingGuidesPath || isBestPickPath
	const canonical = `${SITE_HOST}${meta.canonical}`

	return (
		<SiteMapWrapper>
			<Layout inverseLinkColors={true} meta={{ ...meta, canonical: canonical }}>
				<JsonLd item={SearchSchema(meta.canonical)} />
				<SimpleHeader
					breadcrumbs={{ primary: { name: 'Sitemap', slug: 'sitemap' } }}
					canonical={canonical}
					isHubTemplate={true}
					shouldShowLeaderboardAds={false}
					title="Site Map"
					subTitle={isOfTypeBuyingGuide ? categoryTitle : `Articles ${year ? `/ ${year}` : ''}`}
				/>
				<PageWrapper>
					<SiteMapCategories>
						<PageLink to="/sitemap/" isbuyingguides={(!isOfTypeBuyingGuide).toString()}>
							Articles
						</PageLink>
						<Divider> | </Divider>
						<PageLink to={`/sitemap/${categorySlug}/`} isbuyingguides={isOfTypeBuyingGuide.toString()}>
							{categoryTitle}
						</PageLink>
					</SiteMapCategories>
					<ContentWrapper>{children}</ContentWrapper>
				</PageWrapper>
			</Layout>
		</SiteMapWrapper>
	)
}
